// apps/web/src/pages/vendas/fechamento/ajustar-rascunho-dialog.tsx — Fase 12, Plano 14 (FECH-02, D-18, A-32; UI-SPEC § Rascunho fiscal).
// 'Ajustar valores': só com o rascunho PRONTO; altera apenas quantidade e valor unitário das linhas. A justificativa é
// obrigatória (mín. 5 caracteres) e fica auditada com antes/depois. Só as linhas alteradas são enviadas.
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { AjustarRascunhoInput, RascunhoFiscalView } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { numeroDoRascunhoParaCampo, rotuloLinhaRascunho } from '../../../lib/vendas/fechamento';
import { lerNumeroDigitado } from '../../../lib/vendas/numero';
import { erroPrecoPv, erroQuantidadePv } from '../../../lib/vendas/validacao-pv';
import { escreverRascunho, MENSAGEM_FALHA_RECARGA_RASCUNHO } from './rascunho-api';
import { AlertaConflitoPv } from '../detalhe/alerta-conflito-pv';

const JUSTIFICATIVA_MINIMA = 5;

interface AjustarRascunhoDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly rascunho: RascunhoFiscalView;
  readonly onAjustado: (rascunho: RascunhoFiscalView) => void;
  /** Recarrega o rascunho (conflito de versão). */
  /** Recarrega o rascunho; devolve se deu certo (só então o conflito é limpo). */
  readonly onRecarregar: () => Promise<boolean>;
}

interface LinhaEdicao {
  readonly chave: string;
  readonly itemRef: string;
  readonly tipo: AjustarRascunhoInput['linhas'][number]['tipo'];
  readonly tintaMaterialId: string | null;
  readonly descricao: string;
  readonly numeroItem: number | null;
  readonly quantidade: string;
  readonly valorUnitario: string;
}

function linhasIniciais(rascunho: RascunhoFiscalView): LinhaEdicao[] {
  return rascunho.itens.flatMap((item) =>
    item.linhas.map((linha, indice) => ({
      chave: `${item.itemRef}:${linha.tipo}:${linha.materialId ?? ''}:${indice}`,
      itemRef: item.itemRef,
      tipo: linha.tipo,
      tintaMaterialId: linha.tipo === 'TINTA' ? linha.materialId : null,
      descricao: linha.descricao,
      numeroItem: linha.numeroItemPv,
      quantidade: numeroDoRascunhoParaCampo(linha.quantidade),
      valorUnitario: numeroDoRascunhoParaCampo(linha.valorUnitario),
    })),
  );
}

export function AjustarRascunhoDialog({ aberto, onAbertoChange, rascunho, onAjustado, onRecarregar }: AjustarRascunhoDialogProps) {
  const [linhas, setLinhas] = useState<LinhaEdicao[]>([]);
  const [justificativa, setJustificativa] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [conflito, setConflito] = useState<string | null>(null);
  const [recarregando, setRecarregando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setLinhas(linhasIniciais(rascunho));
    setJustificativa('');
    setErro(null);
    setConflito(null);
    // Reinicia a edição só ao abrir; uma nova visão do rascunho (após recarregar) também reinicia.
  }, [aberto, rascunho.versao]);

  const originais = useMemo(() => linhasIniciais(rascunho), [rascunho]);
  const alteradas = linhas.filter((linha) => {
    const original = originais.find((candidata) => candidata.chave === linha.chave);
    return original !== undefined && (original.quantidade !== linha.quantidade || original.valorUnitario !== linha.valorUnitario);
  });
  // Primeiro erro das linhas alteradas (ambiguidade "1.500" explicada; teto/casas iguais aos da API).
  const erroLinhas =
    alteradas
      .map((linha) => erroQuantidadePv(lerNumeroDigitado(linha.quantidade), linha.quantidade) ?? erroPrecoPv(lerNumeroDigitado(linha.valorUnitario), linha.valorUnitario))
      .find((mensagem): mensagem is string => mensagem !== null) ?? null;
  const invalidas = erroLinhas !== null;
  const justificativaValida = justificativa.trim().length >= JUSTIFICATIVA_MINIMA;
  const podeSalvar = alteradas.length > 0 && !invalidas && justificativaValida && !enviando;

  const editar = (chave: string, campo: 'quantidade' | 'valorUnitario', texto: string) =>
    setLinhas((atual) => atual.map((linha) => (linha.chave === chave ? { ...linha, [campo]: texto } : linha)));

  async function salvar(): Promise<void> {
    if (!podeSalvar) return;
    setErro(null);
    setConflito(null);
    setEnviando(true);
    try {
      const corpo: AjustarRascunhoInput = {
        versao: rascunho.versao,
        justificativa: justificativa.trim(),
        linhas: alteradas.map((linha) => ({
          itemRef: linha.itemRef,
          tipo: linha.tipo,
          ...(linha.tintaMaterialId ? { tintaMaterialId: linha.tintaMaterialId } : {}),
          quantidade: lerNumeroDigitado(linha.quantidade),
          valorUnitario: lerNumeroDigitado(linha.valorUnitario),
        })),
      };
      const resultado = await escreverRascunho(rascunho.retornoId, '/ajustar', corpo);
      if (resultado.tipo === 'ok') {
        onAbertoChange(false);
        onAjustado(resultado.view);
      } else if (resultado.tipo === 'conflito') {
        setConflito(resultado.message);
      } else {
        setErro(resultado.message);
      }
    } finally {
      setEnviando(false);
    }
  }

  async function recarregar(): Promise<void> {
    setRecarregando(true);
    try {
      if (!(await onRecarregar())) setErro(MENSAGEM_FALHA_RECARGA_RASCUNHO);
    } finally {
      setRecarregando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (enviando ? undefined : onAbertoChange(proximo))}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Ajustar valores</DialogTitle>
          <DialogDescription>
            Altere só quantidade e valor unitário das linhas necessárias. O ajuste fica registrado com o antes e o depois; recalcular o
            rascunho descarta o ajuste.
          </DialogDescription>
        </DialogHeader>
        {conflito && <AlertaConflitoPv mensagem={conflito} onRecarregar={() => void recarregar()} recarregando={recarregando} />}
        <div className="flex flex-col gap-2">
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr>
                <th scope="col" className="py-1 pr-2 text-xs font-semibold uppercase tracking-wider">Linha</th>
                <th scope="col" className="w-28 px-2 py-1 text-right text-xs font-semibold uppercase tracking-wider">Qtd</th>
                <th scope="col" className="w-32 px-2 py-1 text-right text-xs font-semibold uppercase tracking-wider">Valor unit.</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => (
                <tr key={linha.chave} className="border-t">
                  <td className="py-1.5 pr-2">
                    <p className="font-medium">
                      {rotuloLinhaRascunho(linha.tipo)}
                      {linha.numeroItem !== null && <span className="text-muted-foreground"> · item {linha.numeroItem}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{linha.descricao}</p>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      aria-label={`Quantidade — ${rotuloLinhaRascunho(linha.tipo)} ${linha.descricao}`}
                      inputMode="decimal"
                      className="text-right tabular-nums"
                      value={linha.quantidade}
                      disabled={enviando}
                      onChange={(e) => editar(linha.chave, 'quantidade', e.target.value)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      aria-label={`Valor unitário — ${rotuloLinhaRascunho(linha.tipo)} ${linha.descricao}`}
                      inputMode="decimal"
                      className="text-right tabular-nums"
                      value={linha.valorUnitario}
                      disabled={enviando}
                      onChange={(e) => editar(linha.chave, 'valorUnitario', e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {erroLinhas && <FormMessage>{erroLinhas}</FormMessage>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ajuste-justificativa">Justificativa do ajuste (obrigatória)</Label>
          <Textarea
            id="ajuste-justificativa"
            value={justificativa}
            disabled={enviando}
            onChange={(e) => setJustificativa(e.target.value)}
            aria-describedby="ajuste-justificativa-ajuda"
          />
          <p id="ajuste-justificativa-ajuda" className="text-xs text-muted-foreground">
            Informe ao menos {JUSTIFICATIVA_MINIMA} caracteres. Fica registrada na auditoria.
          </p>
        </div>
        {erro && <FormMessage>{erro}</FormMessage>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" onClick={() => void salvar()} disabled={!podeSalvar}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            Salvar ajuste
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
